import React from "react";
import { MousePointerClick, Eye, ArrowUpRight } from "lucide-react";

interface BannerPerformanceItem {
  entityId: number;
  views: number;
  clicks: number;
  clickThroughRate: number;
}

interface BannerPerformanceProps {
  banners?: BannerPerformanceItem[];
}

const BannerPerformance: React.FC<BannerPerformanceProps> = ({
  banners = [],
}) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* HEADER */}

      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <MousePointerClick size={18} />
            </div>

            <div>
              <h2 className="text-sm font-bold text-gray-800">
                Banner Performance
              </h2>

              <p className="text-[10px] text-gray-400 mt-0.5">
                Most viewed promotional banners
              </p>
            </div>
          </div>

          <span className="text-[10px] text-gray-400">
            Top {banners.length}
          </span>
        </div>
      </div>

      {/* TABLE */}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50/70">
              <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400">
                Banner
              </th>

              <th className="px-4 py-3 text-right text-[10px] font-semibold text-gray-400">
                Views
              </th>

              <th className="px-4 py-3 text-right text-[10px] font-semibold text-gray-400">
                Clicks
              </th>

              <th className="px-5 py-3 text-right text-[10px] font-semibold text-gray-400">
                CTR
              </th>
            </tr>
          </thead>

          <tbody>
            {banners.length > 0 ? (
              banners.map((banner, index) => (
                <tr
                  key={banner.entityId}
                  className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <span
                        className={`
                          w-7 h-7
                          rounded-lg
                          flex items-center justify-center
                          text-[10px]
                          font-bold
                          ${
                            index === 0
                              ? "bg-logoHeading/10 text-logoHeading"
                              : "bg-gray-100 text-gray-500"
                          }
                        `}
                      >
                        {index + 1}
                      </span>

                      <div>
                        <p className="text-xs font-semibold text-gray-700">
                          Banner #{banner.entityId}
                        </p>

                        <p className="text-[9px] text-gray-400 mt-0.5">
                          Promotional banner
                        </p>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3.5 text-right">
                    <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                      <Eye size={13} className="text-blue-500" />
                      {banner.views.toLocaleString()}
                    </div>
                  </td>

                  <td className="px-4 py-3.5 text-right">
                    <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                      <MousePointerClick
                        size={13}
                        className="text-emerald-500"
                      />
                      {banner.clicks.toLocaleString()}
                    </div>
                  </td>

                  <td className="px-5 py-3.5 text-right">
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-logoHeading">
                      {banner.clickThroughRate.toFixed(2)}%
                      <ArrowUpRight size={12} />
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="py-12 text-center">
                  <p className="text-xs text-gray-400">
                    No banner activity available.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BannerPerformance;
